import { useEffect, useState } from 'react';

import { Badge } from '@/design-system/primitives';
import {
  type AutoAssignRule,
  useAdminRoster,
  useApplyAssignRules,
  useSaveSupportSettings,
  useSupportSettings,
} from '@/lib/api/ticketHooks';
import { DISPOSITIONS } from './ticketUi';

/**
 * Support settings — auto-assignment rules. Each rule: a disposition scope, a
 * pool of admins, and a strategy (round-robin or least-loaded). First enabled
 * matching rule wins. Rules apply to NEWLY created tickets (manual unassign
 * sticks); "Apply to unassigned" backfills the current queue.
 */
export function SupportSettingsScreen() {
  const { data, isLoading } = useSupportSettings();
  const roster = useAdminRoster();
  const save = useSaveSupportSettings();
  const apply = useApplyAssignRules();
  const [rules, setRules] = useState<AutoAssignRule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [progress, setProgress] = useState<{
    assigned: number;
    remaining: number | null;
    running: boolean;
    unmatched: number;
  } | null>(null);

  const runApply = async () => {
    let total = 0;
    setProgress({ assigned: 0, remaining: null, running: true, unmatched: 0 });
    try {
      for (;;) {
        const r = await apply.mutateAsync(25);
        total += r.assigned;
        if (r.remaining === 0 || r.assigned === 0) {
          setProgress({
            assigned: total,
            remaining: r.remaining,
            running: false,
            unmatched: r.assigned === 0 ? r.remaining : 0,
          });
          return;
        }
        setProgress({ assigned: total, remaining: r.remaining, running: true, unmatched: 0 });
      }
    } catch {
      setProgress((p) => (p ? { ...p, running: false } : null));
    }
  };

  useEffect(() => {
    if (data && !dirty) setRules(data.auto_assign_rules ?? []);
  }, [data, dirty]);

  const admins = roster.data?.admins ?? [];
  const mutate = (fn: (rs: AutoAssignRule[]) => AutoAssignRule[]) => {
    setRules(fn);
    setDirty(true);
  };

  const addRule = () =>
    mutate((rs) => [
      ...rs,
      {
        id: crypto.randomUUID(),
        name: `Rule ${rs.length + 1}`,
        dispositions: ['all'],
        pool: [],
        strategy: 'round_robin',
        enabled: true,
      },
    ]);

  const moveRule = (idx: number, dir: -1 | 1) =>
    mutate((rs) => {
      const j = idx + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  if (isLoading) {
    return <div className="type-label-sm text-ink-muted py-40">Loading settings…</div>;
  }

  return (
    <div className="flex flex-col gap-20 max-w-[52rem]">
      <div>
        <h1 className="type-headline-md">Support settings</h1>
        <p className="type-label-sm text-ink-muted mt-4">
          Auto-assignment: new tickets get an assignee from the{' '}
          <strong>first enabled rule that matches, top to bottom</strong> — put specific
          rules above the catch-all, or the catch-all shadows them. Round-robin rotates
          through the pool; least-loaded picks whoever has the fewest open tickets.
          Manually unassigned tickets stay unassigned.
        </p>
      </div>

      {rules.map((rule, idx) => (
        <section key={idx} className="border border-line rounded-lg bg-raised p-16 flex flex-col gap-12">
          <div className="flex items-center gap-10">
            <div className="flex flex-col shrink-0">
              <button
                type="button"
                onClick={() => moveRule(idx, -1)}
                disabled={idx === 0}
                aria-label="Move rule up"
                className="text-10 text-ink-muted hover:text-ink disabled:opacity-30 leading-none"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => moveRule(idx, 1)}
                disabled={idx === rules.length - 1}
                aria-label="Move rule down"
                className="text-10 text-ink-muted hover:text-ink disabled:opacity-30 leading-none"
              >
                ▼
              </button>
            </div>
            <span className="text-10 font-mono text-ink-muted shrink-0">#{idx + 1}</span>
            <input
              value={rule.name}
              onChange={(e) => mutate((rs) => rs.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)))}
              className="type-label-md font-semibold bg-transparent border-b border-transparent focus:border-line outline-none flex-1 min-w-0"
              aria-label="Rule name"
            />
            <label className="flex items-center gap-6 type-label-sm text-ink-muted">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => mutate((rs) => rs.map((r, i) => (i === idx ? { ...r, enabled: e.target.checked } : r)))}
              />
              Enabled
            </label>
            <button
              type="button"
              onClick={() => mutate((rs) => rs.filter((_, i) => i !== idx))}
              className="type-label-sm text-danger hover:underline"
            >
              Remove
            </button>
          </div>

          <div className="flex flex-col gap-6">
            <span className="text-10 uppercase text-ink-muted">Applies to</span>
            <div className="flex items-center gap-10 flex-wrap">
              <label className="flex items-center gap-6 type-label-sm">
                <input
                  type="checkbox"
                  checked={rule.dispositions.includes('all')}
                  onChange={(e) =>
                    mutate((rs) =>
                      rs.map((r, i) =>
                        i === idx ? { ...r, dispositions: e.target.checked ? ['all'] : [] } : r,
                      ),
                    )
                  }
                />
                All tickets
              </label>
              {!rule.dispositions.includes('all') &&
                DISPOSITIONS.map((d) => (
                  <label key={d} className="flex items-center gap-6 type-label-sm">
                    <input
                      type="checkbox"
                      checked={rule.dispositions.includes(d)}
                      onChange={(e) =>
                        mutate((rs) =>
                          rs.map((r, i) =>
                            i === idx
                              ? {
                                  ...r,
                                  dispositions: e.target.checked
                                    ? [...r.dispositions, d]
                                    : r.dispositions.filter((x) => x !== d),
                                }
                              : r,
                          ),
                        )
                      }
                    />
                    {d}
                  </label>
                ))}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <span className="text-10 uppercase text-ink-muted">Pool ({rule.pool.length})</span>
            <div className="flex items-center gap-10 flex-wrap">
              {admins.map((a) => (
                <label key={a.id} className="flex items-center gap-6 type-label-sm">
                  <input
                    type="checkbox"
                    checked={rule.pool.includes(a.id)}
                    onChange={(e) =>
                      mutate((rs) =>
                        rs.map((r, i) =>
                          i === idx
                            ? {
                                ...r,
                                pool: e.target.checked
                                  ? [...r.pool, a.id]
                                  : r.pool.filter((x) => x !== a.id),
                              }
                            : r,
                        ),
                      )
                    }
                  />
                  {a.display_name || a.email || a.id}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-10">
            <span className="text-10 uppercase text-ink-muted">Strategy</span>
            <select
              value={rule.strategy}
              onChange={(e) =>
                mutate((rs) =>
                  rs.map((r, i) =>
                    i === idx ? { ...r, strategy: e.target.value as AutoAssignRule['strategy'] } : r,
                  ),
                )
              }
              className="type-label-sm px-10 py-6 rounded border border-line bg-raised"
            >
              <option value="round_robin">Round-robin (rotate through pool)</option>
              <option value="least_loaded">Least-loaded (fewest open tickets)</option>
            </select>
            {rule.pool.length === 0 && (
              <Badge tone="warning" size="sm">
                pool empty — rule inert
              </Badge>
            )}
          </div>
        </section>
      ))}

      <div className="flex items-center gap-10 flex-wrap">
        <button
          type="button"
          onClick={addRule}
          className="type-label-sm px-12 py-8 rounded border border-line hover:bg-raised"
        >
          + Add rule
        </button>
        <button
          type="button"
          onClick={() => save.mutate(rules, { onSuccess: () => setDirty(false) })}
          disabled={!dirty || save.isPending}
          className="type-label-sm px-12 py-8 rounded bg-ink text-page hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : dirty ? 'Save rules' : 'Saved ✓'}
        </button>
        <button
          type="button"
          onClick={runApply}
          disabled={progress?.running || dirty}
          title={dirty ? 'Save rules first' : 'Run the rules over currently-unassigned tickets'}
          className="type-label-sm px-12 py-8 rounded border border-accent text-accent bg-accent-wash hover:opacity-90 disabled:opacity-50"
        >
          {progress?.running ? 'Applying…' : 'Apply to unassigned now'}
        </button>
      </div>

      {progress && (
        <div className="flex flex-col gap-6 max-w-[28rem]">
          <div className="h-6 rounded-pill bg-sunken overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{
                width:
                  progress.remaining == null
                    ? '5%'
                    : progress.assigned + progress.remaining === 0
                      ? '100%'
                      : `${Math.round((progress.assigned / (progress.assigned + progress.remaining)) * 100)}%`,
              }}
            />
          </div>
          <span className="type-label-sm text-ink-muted" role="status">
            {progress.running
              ? `Assigned ${progress.assigned}${progress.remaining != null ? ` · ${progress.remaining} remaining` : '…'}`
              : progress.unmatched > 0
                ? `Done — assigned ${progress.assigned}; ${progress.unmatched} left unmatched (no enabled rule covers them)`
                : `Done — assigned ${progress.assigned}, none remaining`}
          </span>
        </div>
      )}

      <p className="type-label-sm text-ink-muted">
        Rules also run automatically every sweep tick (~15 min): anything unassigned —
        including manually unassigned tickets — gets picked up while a matching rule is
        enabled. To keep a ticket unowned, move it to a waiting status or disable the rule.
      </p>
    </div>
  );
}
